import { formatEventDate, formatEventTimeWindow, readerTimezone } from '@h2hspark/ui';
import { Clock, MapPin, Sparkles } from 'lucide-react';
import * as React from 'react';

import { usePublicEvent } from '../event-discovery/api';

import { CheckoutCoverBand } from './checkout-cover-band-panel';
import { ORDER_SUMMARY_INCLUDES_NOTE, ORDER_SUMMARY_LABEL, signUpSummaryRows } from './constants';

/**
 * **What a visitor is signing up for, beside the sign-up** (`MEM-058` AC-5, TW 44159462).
 *
 * `Web App.dc.html`'s reservation flow draws the order summary beside every step, account
 * creation included. Ours signs up at `/app/join` and pays at the checkout, so until 2026-09-24
 * the summary lived on the checkout alone. Sumit, on the task: *"ok, go ahead and implement as
 * designed. On phone it can stack."* So this is the checkout's card, drawn from the public event:
 * the same cover band, the date eyebrow, the name, the hours, *Venue, City*, the lines, and the
 * note beneath them.
 *
 * **On a paid ticket the fee and the total are placeholders, not a different card.** No ticket is
 * held until the checkout, and the fee is the server's to compute there (`MEM-050`), so both rows
 * are drawn in their places and say they are settled at payment. The ticket row is known: it is the
 * ticket the visitor pressed Reserve for, which is the one the checkout address names, or the
 * organizer's first when it names none.
 *
 * **A free event, or a free ticket, is drawn as the checkout draws a free order** (TW 44206238):
 * the ticket at `$0.00`, no Service fee row, and the total at `$0.00` (`MEM-050` AC-5, Sumit
 * 2026-09-04). `signUpSummaryRows` makes that decision; this component draws what it returns.
 *
 * Nothing while the event is loading or when it cannot be read: a sign-up must never wait on, or
 * fail because of, the card beside it.
 */
export function SignUpEventSummary({
  eventId,
  ticketTypeId,
}: {
  readonly eventId: string;
  readonly ticketTypeId: string | null;
}): React.ReactElement | null {
  const event = usePublicEvent(eventId);
  const detail = event.data;
  if (detail === undefined) return null;

  const reader = readerTimezone();
  const cardDate = formatEventDate({ startsAt: detail.startsAt, timezone: detail.timezone });
  const cardHours = formatEventTimeWindow({
    startsAt: detail.startsAt,
    endsAt: detail.endsAt,
    timezone: detail.timezone,
    reader,
  });
  const venueLine =
    [detail.location.venueName, detail.location.city]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .join(', ') || null;
  const rows = signUpSummaryRows(detail.ticketTypes, ticketTypeId, detail.freeAdmission);

  return (
    <div
      data-testid="sign-up-event-summary"
      className="min-w-0 overflow-hidden rounded-xl border border-border-subtle bg-card shadow-md lg:sticky lg:top-[88px]"
    >
      <CheckoutCoverBand cover={detail.coverImage} />
      <div className="space-y-1 p-4 sm:p-5">
        <p className="font-brand text-xs font-bold uppercase tracking-caps text-brand-accent">
          {cardDate}
        </p>
        <h2 className="font-brand text-lg font-bold text-foreground">{detail.name}</h2>
        {cardHours === '' ? null : (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-[15px] w-[15px] shrink-0" aria-hidden />
            <span>{cardHours}</span>
          </p>
        )}
        {venueLine === null ? null : (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-[15px] w-[15px] shrink-0" aria-hidden />
            <span>{venueLine}</span>
          </p>
        )}
        <div className="!mt-4 border-t border-divider pt-3.5">
          <dl aria-label={ORDER_SUMMARY_LABEL}>
            {rows.map((row) =>
              row.kind === 'total' ? (
                <div
                  key={row.kind}
                  className="mt-2.5 flex items-baseline justify-between border-t border-divider pt-2.5 font-brand text-md font-extrabold text-foreground"
                >
                  <dt>{row.label}</dt>
                  <dd
                    className={
                      row.settledAtPayment
                        ? 'font-sans text-sm font-normal text-muted-foreground'
                        : undefined
                    }
                  >
                    {row.amount}
                  </dd>
                </div>
              ) : (
                <div
                  key={row.kind}
                  className="mb-2 flex items-baseline justify-between text-sm text-text-body"
                >
                  <dt>{row.label}</dt>
                  <dd className={row.settledAtPayment ? 'text-muted-foreground' : undefined}>
                    {row.amount}
                  </dd>
                </div>
              ),
            )}
          </dl>
        </div>
        <p className="!mt-4 flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2.5 text-xs text-brand">
          <Sparkles className="h-[15px] w-[15px] shrink-0" aria-hidden />
          {ORDER_SUMMARY_INCLUDES_NOTE}
        </p>
      </div>
    </div>
  );
}
